import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, DollarSign, Target, Loader2, BarChart3, Activity } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ChannelPerformancePage() {
  const [ibs, setIbs] = useState([]);
  const [selectedIB, setSelectedIB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marketingData, setMarketingData] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [operationMetrics, setOperationMetrics] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchIBs();
  }, []);

  // Fetch real data when IB changes
  useEffect(() => {
    if (selectedIB) {
      fetchPerformanceData(selectedIB.id);
    }
  }, [selectedIB?.id]);

  const fetchIBs = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) { navigate('/login'); return; }
      const response = await axios.get(`${API}/ib/list`, { headers: { Authorization: `Bearer ${token}` } });
      setIbs(response.data);
      const savedIB = localStorage.getItem('selectedIB');
      if (savedIB) {
        const ib = response.data.find(i => i.id === savedIB);
        if (ib) setSelectedIB(ib);
        else if (response.data.length > 0) setSelectedIB(response.data[0]);
      } else if (response.data.length > 0) {
        setSelectedIB(response.data[0]);
        localStorage.setItem('selectedIB', response.data[0].id);
      }
    } catch { toast.error('Failed to load IBs'); }
    finally { setLoading(false); }
  };

  const fetchPerformanceData = async (ibId) => {
    setLoadingData(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const now = new Date();
      const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

      const [dailyRes, opsRes] = await Promise.all([
        axios.get(`${API}/historical/2026/daily-data/${ibId}?month=${monthKey}`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/signal-metrics/${ibId}/all`, { headers }).catch(() => ({ data: { metrics: null } })),
      ]);

      setMarketingData(dailyRes.data?.marketing || []);
      setSalesData(dailyRes.data?.sales || []);
      setOperationMetrics(opsRes.data?.metrics || null);
    } catch (e) {
      console.error('Failed to fetch performance data:', e);
    } finally {
      setLoadingData(false);
    }
  };

  // Compute Marketing metrics from real data
  const marketingMetrics = useMemo(() => {
    if (!marketingData.length) return null;
    const calcTotal = (obj) => obj ? Object.values(obj).reduce((s, v) => s + (parseFloat(v) || 0), 0) : 0;
    const totalSpend = marketingData.reduce((s, d) => s + calcTotal(d.spend), 0);
    const totalImpressions = marketingData.reduce((s, d) => s + calcTotal(d.impressions), 0);
    const totalClicks = marketingData.reduce((s, d) => s + calcTotal(d.clicks), 0);
    const totalLPV = marketingData.reduce((s, d) => s + (d.lpv || 0), 0);
    const totalCTA = marketingData.reduce((s, d) => s + (d.cta || 0), 0);
    const totalSubsTG = marketingData.reduce((s, d) => s + (d.subsTG || 0), 0);
    const totalSubsBot = marketingData.reduce((s, d) => s + (d.subsBot || 0), 0);
    const totalSubs = totalSubsTG + totalSubsBot;
    const totalLeads = marketingData.reduce((s, d) => s + (d.leads || 0), 0);

    const ctr = totalImpressions > 0 ? (totalClicks / totalImpressions * 100) : 0;
    const cpm = totalImpressions > 0 ? (totalSpend / totalImpressions * 1000) : 0;
    const cps = totalSubs > 0 ? (totalSpend / totalSubs) : 0;
    const cpl = totalLeads > 0 ? (totalSpend / totalLeads) : 0;
    const lpvRate = totalClicks > 0 ? (totalLPV / totalClicks * 100) : 0;
    const ctaRate = totalLPV > 0 ? (totalCTA / totalLPV * 100) : 0;

    return {
      totalSpend, totalImpressions, totalClicks, totalLPV, totalCTA, totalSubs, totalLeads,
      ctr, cpm, cps, cpl, lpvRate, ctaRate,
      funnelScores: [
        { label: 'Impressions', value: totalImpressions, score: Math.min(100, Math.round(totalImpressions / 100)) },
        { label: 'Click-Through Rate', value: `${ctr.toFixed(2)}%`, score: Math.min(100, Math.round(ctr * 10)) },
        { label: 'Landing Page Views', value: totalLPV, score: Math.min(100, Math.round(lpvRate)) },
        { label: 'Subscriber Conversion', value: totalSubs, score: Math.min(100, Math.round(ctaRate)) },
      ],
      metricScores: [
        { label: 'CPM (Cost per 1000 Impressions)', value: `RM ${cpm.toFixed(2)}`, score: Math.min(100, cpm > 0 ? Math.round(Math.max(0, 100 - cpm)) : 0) },
        { label: 'CTR (Click-Through Rate)', value: `${ctr.toFixed(2)}%`, score: Math.min(100, Math.round(ctr * 10)) },
        { label: 'CPS (Cost per Subscriber)', value: `RM ${cps.toFixed(2)}`, score: Math.min(100, cps > 0 ? Math.round(Math.max(0, 100 - cps / 2)) : 0) },
        { label: 'CPL (Cost per Lead)', value: `RM ${cpl.toFixed(2)}`, score: Math.min(100, cpl > 0 ? Math.round(Math.max(0, 100 - cpl / 5)) : 0) },
      ],
    };
  }, [marketingData]);

  // Compute Sales metrics from real data
  const salesMetrics = useMemo(() => {
    if (!salesData.length) return null;
    const totalLeads = salesData.reduce((s, d) => {
      return s + (d.leadsM?.new || 0) + (d.leadsM?.prospect || 0) + (d.leadsM?.vip || 0)
        + (d.leadsS?.new || 0) + (d.leadsS?.prospect || 0) + (d.leadsS?.vip || 0)
        + (d.leadsIB?.new || 0) + (d.leadsIB?.prospect || 0) + (d.leadsIB?.vip || 0)
        + (d.leads || 0);
    }, 0);
    const totalClosed = salesData.reduce((s, d) => s + (d.newClosed || d.closed || 0) + (d.vipClosed || 0), 0);
    const totalGreeting = salesData.reduce((s, d) => s + (d.greeting || 0), 0);
    const totalActive = salesData.reduce((s, d) => {
      const brokerKeys = ['brokerA', 'brokerB', 'brokerC', 'brokerD', 'brokerE'];
      return s + brokerKeys.reduce((bs, k) => bs + (d[k]?.active || 0), 0);
    }, 0);
    const totalCommission = salesData.reduce((s, d) => {
      const brokerKeys = ['brokerA', 'brokerB', 'brokerC', 'brokerD', 'brokerE'];
      return s + brokerKeys.reduce((bs, k) => bs + (d[k]?.commission || 0), 0);
    }, 0);

    const conversionRate = totalLeads > 0 ? (totalClosed / totalLeads * 100) : 0;
    const greetingRate = totalLeads > 0 ? (totalGreeting / totalLeads * 100) : 0;
    const activationRate = totalClosed > 0 ? (totalActive / totalClosed * 100) : 0;

    return {
      totalLeads, totalClosed, totalActive, totalCommission, conversionRate, greetingRate, activationRate,
      funnelScores: [
        { label: 'Lead Generation', value: totalLeads, score: Math.min(100, Math.round(totalLeads / 5)) },
        { label: 'Greeting', value: totalGreeting, score: Math.min(100, Math.round(greetingRate)) },
        { label: 'Closing Rate', value: `${conversionRate.toFixed(1)}%`, score: Math.min(100, Math.round(conversionRate * 2)) },
        { label: 'Retention', value: totalActive, score: Math.min(100, Math.round(activationRate)) },
      ],
      metricScores: [
        { label: 'Conversion Rate (Lead to Client)', value: `${conversionRate.toFixed(1)}%`, score: Math.min(100, Math.round(conversionRate * 2)) },
        { label: 'Activation Rate', value: `${activationRate.toFixed(1)}%`, score: Math.min(100, Math.round(activationRate)) },
        { label: 'Response Time (Greeting Rate)', value: `${greetingRate.toFixed(1)}%`, score: Math.min(100, Math.round(greetingRate)) },
      ],
    };
  }, [salesData]);

  // Operation metrics from API
  const opsMetrics = useMemo(() => {
    if (!operationMetrics) return null;
    const { total_signals, total_win, total_loss, total_breakeven, total_pips_win, total_pips_loss, net_pips, win_rate } = operationMetrics;
    return {
      funnelScores: [
        { label: 'Signal Quality', value: `${total_signals} signals`, score: Math.min(100, Math.round(win_rate)) },
        { label: 'Win Rate', value: `${win_rate}%`, score: Math.min(100, Math.round(win_rate)) },
        { label: 'Consistency', value: `${net_pips} pips`, score: Math.min(100, net_pips > 0 ? Math.round(Math.min(100, net_pips / 10)) : 0) },
      ],
      metricScores: [
        { label: 'Signal Accuracy (Win Rate)', value: `${win_rate}%`, score: Math.min(100, Math.round(win_rate)) },
        { label: 'Risk Management (Win/Loss)', value: `${total_win}W / ${total_loss}L`, score: total_signals > 0 ? Math.min(100, Math.round((total_win + total_breakeven) / total_signals * 100)) : 0 },
        { label: 'Net Pips Performance', value: `${net_pips >= 0 ? '+' : ''}${net_pips}`, score: Math.min(100, net_pips > 0 ? Math.round(Math.min(100, net_pips / 5)) : 0) },
      ],
    };
  }, [operationMetrics]);

  const getScoreBadge = (score) => {
    if (score >= 80) return { className: 'bg-[#06D6A0]/20 text-[#06D6A0] border-[#06D6A0]/40', label: 'Excellent' };
    if (score >= 60) return { className: 'bg-[#FFB703]/20 text-[#FFB703] border-[#FFB703]/40', label: 'Good' };
    if (score >= 40) return { className: 'bg-[#F97316]/20 text-[#F97316] border-[#F97316]/40', label: 'Average' };
    return { className: 'bg-[#E63946]/20 text-[#E63946] border-[#E63946]/40', label: 'Needs Attention' };
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#06D6A0';
    if (score >= 60) return '#FFB703';
    if (score >= 40) return '#F97316';
    return '#E63946';
  };

  const renderFunnelScores = (scores) => (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      {scores.map((item) => (
        <div key={item.label} className="border border-[hsl(var(--border))] rounded-lg p-4 bg-[hsl(var(--surface))]">
          <p className="text-xs text-[hsl(var(--text-muted))] mb-2">{item.label}</p>
          <Badge className={`border ${getScoreBadge(item.score).className}`}>
            {getScoreBadge(item.score).label}
          </Badge>
          <p className="text-sm text-[hsl(var(--text-muted))] mt-2">Score: {item.score}/100</p>
          <p className="text-xs text-[hsl(var(--text-secondary))] mt-1">{item.value}</p>
        </div>
      ))}
    </div>
  );

  const renderMetricScores = (scores) => (
    <div className="space-y-4">
      {scores.map((item) => (
        <div key={item.label} className="flex items-center justify-between p-3 bg-[hsl(var(--surface))] rounded">
          <div className="flex-1">
            <p className="text-[hsl(var(--text-primary))] mb-1">{item.label}</p>
            <p className="text-xs text-[hsl(var(--text-muted))]">{item.value}</p>
            <div className="w-full bg-[hsl(var(--surface-elevated))] rounded-full h-2 mt-1">
              <div className="h-2 rounded-full transition-all duration-500" style={{ width: `${item.score}%`, backgroundColor: getScoreColor(item.score) }}></div>
            </div>
          </div>
          <span className="ml-4 text-xl font-mono" style={{ color: getScoreColor(item.score) }}>{item.score}</span>
        </div>
      ))}
    </div>
  );

  const renderNoData = (label) => (
    <div className="text-center py-16 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl">
      <BarChart3 size={48} className="mx-auto text-[hsl(var(--text-muted))]/30 mb-4" />
      <p className="text-[hsl(var(--text-secondary))] font-medium">Tiada data {label} untuk bulan ini</p>
      <p className="text-sm text-[hsl(var(--text-muted))] mt-1">Sila masukkan data harian untuk melihat prestasi</p>
    </div>
  );

  if (loading) {
    return (
      <ResponsiveLayout>
        <div className="flex-1 flex items-center justify-center min-h-screen bg-[hsl(var(--background))]">
          <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" />
        </div>
      </ResponsiveLayout>
    );
  }

  return (
    <ResponsiveLayout>
      <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] px-4 lg:px-6 py-4 lg:py-6">
        <div className="mb-4 lg:mb-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--brand-orange))] flex items-center justify-center">
              <Activity size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl lg:text-2xl font-bold text-[hsl(var(--text-primary))]" data-testid="page-title">Channel Performance</h1>
              <p className="text-xs lg:text-sm text-[hsl(var(--text-muted))]">Department-level Diagnosis</p>
            </div>
          </div>
        </div>

        <div className="mb-4 lg:mb-6">
          <label className="text-xs uppercase tracking-wider text-[hsl(var(--text-muted))] mb-2 block">Select IB</label>
          <Select value={selectedIB?.id} onValueChange={(value) => { setSelectedIB(ibs.find(ib => ib.id === value)); localStorage.setItem('selectedIB', value); window.dispatchEvent(new Event('ibChanged')); }}>
            <SelectTrigger className="w-full sm:w-64 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-[hsl(var(--text-primary))]" data-testid="ib-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-h-[240px] overflow-y-auto">
              {ibs.map(ib => (
                <SelectItem key={ib.id} value={ib.id} className="text-[hsl(var(--text-primary))]">{ib.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loadingData && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--primary))] mr-2" />
            <span className="text-[hsl(var(--text-muted))]">Loading performance data...</span>
          </div>
        )}

        {selectedIB && !loadingData && (
          <Tabs defaultValue="marketing" className="w-full">
            <TabsList className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] mb-4 lg:mb-6 w-full sm:w-auto overflow-x-auto">
              <TabsTrigger value="marketing" data-testid="tab-marketing"><TrendingUp size={14} className="mr-1.5" />Marketing</TabsTrigger>
              <TabsTrigger value="sales" data-testid="tab-sales"><DollarSign size={14} className="mr-1.5" />Sales</TabsTrigger>
              <TabsTrigger value="operation" data-testid="tab-operation"><Target size={14} className="mr-1.5" />Operation</TabsTrigger>
            </TabsList>

            <TabsContent value="marketing">
              {marketingMetrics ? (
                <div className="space-y-4 lg:space-y-6">
                  <div className="border border-[hsl(var(--border))] bg-[hsl(var(--card))] rounded-lg p-4 lg:p-6">
                    <h3 className="text-lg lg:text-xl font-semibold mb-4 text-[hsl(var(--text-primary))]">Marketing Funnel Score</h3>
                    {renderFunnelScores(marketingMetrics.funnelScores)}
                  </div>
                  <div className="border border-[hsl(var(--border))] bg-[hsl(var(--card))] rounded-lg p-4 lg:p-6">
                    <h3 className="text-lg lg:text-xl font-semibold mb-4 text-[hsl(var(--text-primary))]">Marketing Metric Scores</h3>
                    {renderMetricScores(marketingMetrics.metricScores)}
                  </div>
                  <div className="border border-[hsl(var(--border))] bg-[hsl(var(--card))] rounded-lg p-4">
                    <h3 className="text-sm font-semibold mb-3 text-[hsl(var(--text-secondary))]">Summary</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div><p className="text-[hsl(var(--text-muted))] text-xs">Total Spend</p><p className="font-semibold text-[hsl(var(--text-primary))]">RM {marketingMetrics.totalSpend.toLocaleString()}</p></div>
                      <div><p className="text-[hsl(var(--text-muted))] text-xs">Impressions</p><p className="font-semibold text-[hsl(var(--text-primary))]">{marketingMetrics.totalImpressions.toLocaleString()}</p></div>
                      <div><p className="text-[hsl(var(--text-muted))] text-xs">Subscribers</p><p className="font-semibold text-[hsl(var(--text-primary))]">{marketingMetrics.totalSubs.toLocaleString()}</p></div>
                      <div><p className="text-[hsl(var(--text-muted))] text-xs">Leads</p><p className="font-semibold text-[hsl(var(--text-primary))]">{marketingMetrics.totalLeads.toLocaleString()}</p></div>
                    </div>
                  </div>
                </div>
              ) : renderNoData('Marketing')}
            </TabsContent>

            <TabsContent value="sales">
              {salesMetrics ? (
                <div className="space-y-4 lg:space-y-6">
                  <div className="border border-[hsl(var(--border))] bg-[hsl(var(--card))] rounded-lg p-4 lg:p-6">
                    <h3 className="text-lg lg:text-xl font-semibold mb-4 text-[hsl(var(--text-primary))]">Sales Funnel Score</h3>
                    {renderFunnelScores(salesMetrics.funnelScores)}
                  </div>
                  <div className="border border-[hsl(var(--border))] bg-[hsl(var(--card))] rounded-lg p-4 lg:p-6">
                    <h3 className="text-lg lg:text-xl font-semibold mb-4 text-[hsl(var(--text-primary))]">Sales Metric Scores</h3>
                    {renderMetricScores(salesMetrics.metricScores)}
                  </div>
                  <div className="border border-[hsl(var(--border))] bg-[hsl(var(--card))] rounded-lg p-4">
                    <h3 className="text-sm font-semibold mb-3 text-[hsl(var(--text-secondary))]">Summary</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div><p className="text-[hsl(var(--text-muted))] text-xs">Total Leads</p><p className="font-semibold text-[hsl(var(--text-primary))]">{salesMetrics.totalLeads.toLocaleString()}</p></div>
                      <div><p className="text-[hsl(var(--text-muted))] text-xs">Total Closed</p><p className="font-semibold text-[hsl(var(--text-primary))]">{salesMetrics.totalClosed.toLocaleString()}</p></div>
                      <div><p className="text-[hsl(var(--text-muted))] text-xs">Active Clients</p><p className="font-semibold text-[hsl(var(--text-primary))]">{salesMetrics.totalActive.toLocaleString()}</p></div>
                      <div><p className="text-[hsl(var(--text-muted))] text-xs">Commission</p><p className="font-semibold text-[hsl(var(--text-primary))]">${salesMetrics.totalCommission.toLocaleString()}</p></div>
                    </div>
                  </div>
                </div>
              ) : renderNoData('Sales')}
            </TabsContent>

            <TabsContent value="operation">
              {opsMetrics ? (
                <div className="space-y-4 lg:space-y-6">
                  <div className="border border-[hsl(var(--border))] bg-[hsl(var(--card))] rounded-lg p-4 lg:p-6">
                    <h3 className="text-lg lg:text-xl font-semibold mb-4 text-[hsl(var(--text-primary))]">Operation Funnel Score</h3>
                    {renderFunnelScores(opsMetrics.funnelScores)}
                  </div>
                  <div className="border border-[hsl(var(--border))] bg-[hsl(var(--card))] rounded-lg p-4 lg:p-6">
                    <h3 className="text-lg lg:text-xl font-semibold mb-4 text-[hsl(var(--text-primary))]">Operation Metric Scores</h3>
                    {renderMetricScores(opsMetrics.metricScores)}
                  </div>
                </div>
              ) : renderNoData('Operation')}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </ResponsiveLayout>
  );
}
